import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

import SolanaProvider from './providers/SolanaProvider';
import MainMenu from './components/MainMenu';
import GameScreen from './components/GameScreen';
import LoadoutSelector from './components/LoadoutSelector';
import Shop from './components/Shop';
import Leaderboard from './components/Leaderboard';
import DailyChallenges from './components/DailyChallenges';
import BossRoster from './components/BossRoster';
import Bestiary from './components/Bestiary';
import LobbyBrowser from './components/LobbyBrowser';
import MultiplayerLobby from './components/MultiplayerLobby';
import TournamentBrowser from './components/TournamentBrowser';
import TournamentBracket from './components/TournamentBracket';
import { SpectatorBrowser, SpectatorCanvas } from './components/SpectatorView';
import PlayerProfile from './components/PlayerProfile';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to proxy external images for canvas CORS compatibility
const getProxiedImageUrl = (url) => {
  if (!url) return null;
  // If it's an external URL that needs proxying, use the proxy endpoint
  if (url.includes('customer-assets.emergentagent.com')) {
    return `${API}/image-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

// Game screens enum
const SCREENS = {
  MENU: 'menu',
  GAME: 'game',
  SHOP: 'shop',
  LEADERBOARD: 'leaderboard',
  CHALLENGES: 'challenges',
  MULTIPLAYER: 'multiplayer',
  LOBBY: 'lobby',
  TOURNAMENTS: 'tournaments',
  TOURNAMENT_VIEW: 'tournament_view',
  SPECTATE: 'spectate',
  SPECTATE_MATCH: 'spectate_match',
  PROFILE: 'profile',
  LOADOUT: 'loadout',
  BESTIARY: 'bestiary',
  BOSS_ROSTER: 'boss_roster'
};

const GameApp = () => {
  const { publicKey, connected } = useWallet();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [currentScreen, setCurrentScreen] = useState(SCREENS.MENU);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [skins, setSkins] = useState([]);
  const [weapons, setWeapons] = useState([]);
  const [nfts, setNfts] = useState([]);
  const [ownedSkins, setOwnedSkins] = useState(['classic']);
  const [ownedWeapons, setOwnedWeapons] = useState([]);
  const [ownedNfts, setOwnedNfts] = useState([]);

  // ───── Per-wallet inventory cache helpers ─────
  // Persists owned skins/weapons/NFTs to localStorage so they stay across
  // page reloads / app restarts (in addition to being source-of-truth on backend).
  // === ENCRYPTED INVENTORY CACHE PROTOCOL (P-2) ===
  // The cache is XOR-obfuscated + HMAC-checksummed with a key derived from the
  // wallet address. Any manual edit in DevTools invalidates the checksum and the
  // cache is silently discarded, forcing a fresh fetch from the backend (which
  // is the actual source of truth — backend HMAC-signs every purchase row).
  const inventoryCacheKey = useCallback((wallet) => `metal_lego_inv_${wallet}`, []);

  const _seedFromWallet = useCallback((wallet) => {
    const s = `${wallet}|metal-lego-inv-v1`;
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }, []);

  const _xorMask = useCallback((text, seed) => {
    let s = seed >>> 0;
    let out = '';
    for (let i = 0; i < text.length; i++) {
      s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
      out += String.fromCharCode(text.charCodeAt(i) ^ (s & 0xff));
    }
    return out;
  }, []);

  const _checksum = useCallback((text, seed) => {
    let h = seed >>> 0;
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h.toString(16);
  }, []);

  const readInventoryCache = useCallback((wallet) => {
    try {
      const raw = localStorage.getItem(inventoryCacheKey(wallet));
      if (!raw) return null;
      const envelope = JSON.parse(raw);
      // Legacy plaintext cache: ignore + clean up so next write upgrades.
      if (!envelope || envelope.v !== 1 || !envelope.c || !envelope.h) {
        localStorage.removeItem(inventoryCacheKey(wallet));
        return null;
      }
      const seed = _seedFromWallet(wallet);
      const cipher = atob(envelope.c);
      const plaintext = _xorMask(cipher, seed);
      // Verify checksum — any tamper invalidates the cache.
      if (_checksum(plaintext, seed) !== envelope.h) {
        localStorage.removeItem(inventoryCacheKey(wallet));
        return null;
      }
      const parsed = JSON.parse(plaintext);
      return {
        skins: Array.isArray(parsed.skins) ? parsed.skins : null,
        weapons: Array.isArray(parsed.weapons) ? parsed.weapons : null,
        nfts: Array.isArray(parsed.nfts) ? parsed.nfts : null,
      };
    } catch (e) { return null; }
  }, [inventoryCacheKey, _seedFromWallet, _xorMask, _checksum]);

  const writeInventoryCache = useCallback((wallet, patch) => {
    try {
      const existing = readInventoryCache(wallet) || {};
      const merged = { ...existing, ...patch, updated_at: Date.now() };
      const seed = _seedFromWallet(wallet);
      const plaintext = JSON.stringify(merged);
      const cipher = _xorMask(plaintext, seed);
      const envelope = {
        v: 1,
        c: btoa(cipher),
        h: _checksum(plaintext, seed),
      };
      localStorage.setItem(inventoryCacheKey(wallet), JSON.stringify(envelope));
    } catch (e) { /* ignore quota / encoding */ }
  }, [inventoryCacheKey, readInventoryCache, _seedFromWallet, _xorMask, _checksum]);
  // === END ENCRYPTED INVENTORY CACHE PROTOCOL ===
  const [selectedSkin, setSelectedSkin] = useState(null);
  const [selectedNft, setSelectedNft] = useState(null); // Equipped NFT character
  const [leaderboardScores, setLeaderboardScores] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentLobbyId, setCurrentLobbyId] = useState(null);
  const [multiplayerSession, setMultiplayerSession] = useState(null);
  const [currentTournamentId, setCurrentTournamentId] = useState(null);
  const [spectatingLobbyId, setSpectatingLobbyId] = useState(null);
  const [spectatingTournamentCtx, setSpectatingTournamentCtx] = useState(null);
  const [profilePlayerId, setProfilePlayerId] = useState(null);
  const [profileReturnScreen, setProfileReturnScreen] = useState(SCREENS.MENU);
  // Loadout selection — defaults: no extra weapon (default pistol), no grenade
  // Hydrate from localStorage so returning players keep their setup
  const [selectedWeapon, setSelectedWeapon] = useState(() => {
    try { return localStorage.getItem('metal_lego_loadout_weapon') || 'none'; }
    catch (e) { return 'none'; }
  });
  const [bringGrenade, setBringGrenade] = useState(() => {
    try { return localStorage.getItem('metal_lego_loadout_grenade') === 'true'; }
    catch (e) { return false; }
  });
  // Skin id is hydrated lazily after `skins` loads from API
  const [storedSkinId] = useState(() => {
    try { return localStorage.getItem('metal_lego_loadout_skin') || null; }
    catch (e) { return null; }
  });

  // Persist loadout selections whenever they change
  useEffect(() => {
    try { localStorage.setItem('metal_lego_loadout_weapon', selectedWeapon || 'none'); }
    catch (e) { /* ignore quota errors */ }
  }, [selectedWeapon]);
  useEffect(() => {
    try { localStorage.setItem('metal_lego_loadout_grenade', bringGrenade ? 'true' : 'false'); }
    catch (e) { /* ignore */ }
  }, [bringGrenade]);
  useEffect(() => {
    if (selectedSkin?.id) {
      try { localStorage.setItem('metal_lego_loadout_skin', selectedSkin.id); }
      catch (e) { /* ignore */ }
    }
  }, [selectedSkin]);

  // Hydrate screen state from deep-link URL on mount + on URL change.
  // Supported deep links: /profile/:id and /tournament/:id
  useEffect(() => {
    const path = location.pathname;
    const profileMatch = path.match(/^\/profile\/([^/]+)$/);
    const tournamentMatch = path.match(/^\/tournament\/([^/]+)$/);
    if (profileMatch) {
      setProfilePlayerId(profileMatch[1]);
      setProfileReturnScreen(SCREENS.MENU);
      setCurrentScreen(SCREENS.PROFILE);
    } else if (tournamentMatch) {
      setCurrentTournamentId(tournamentMatch[1]);
      setCurrentScreen(SCREENS.TOURNAMENT_VIEW);
    } else if (path === '/' && currentScreen !== SCREENS.MENU) {
      // Browser back → root
      setCurrentScreen(SCREENS.MENU);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Fetch token balance
  const fetchTokenBalance = useCallback(async () => {
    if (!publicKey) return;
    
    try {
      const response = await axios.get(`${API}/wallet/balance/${publicKey.toBase58()}`);
      setTokenBalance(response.data.balance);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  }, [publicKey]);

  // Fetch available skins
  const fetchSkins = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shop/skins`);
      setSkins(response.data);
      
      // Set default skin if not selected — prefer the one persisted in localStorage
      if (!selectedSkin && response.data.length > 0) {
        const stored = storedSkinId ? response.data.find(s => s.id === storedSkinId) : null;
        const defaultSkin = stored || response.data.find(s => s.id === 'classic') || response.data[0];
        setSelectedSkin(defaultSkin);
      }
    } catch (error) {
      console.error('Error fetching skins:', error);
    }
  }, [selectedSkin, storedSkinId]);

  // Fetch available weapons
  const fetchWeapons = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shop/weapons`);
      setWeapons(response.data);
    } catch (error) {
      console.error('Error fetching weapons:', error);
    }
  }, []);

  // Fetch available NFT characters
  const fetchNfts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shop/nfts`);
      setNfts(response.data);
    } catch (error) {
      console.error('Error fetching NFTs:', error);
    }
  }, []);

  // Fetch owned skins
  const fetchOwnedSkins = useCallback(async () => {
    if (!publicKey) return;
    const wallet = publicKey.toBase58();

    // Instant hydrate from cache so UI doesn't flicker on reload
    const cached = readInventoryCache(wallet);
    if (cached?.skins) setOwnedSkins(cached.skins);

    try {
      const response = await axios.get(`${API}/shop/skins/${wallet}`);
      setOwnedSkins(response.data);
      writeInventoryCache(wallet, { skins: response.data });
    } catch (error) {
      console.error('Error fetching owned skins:', error);
    }
  }, [publicKey, readInventoryCache, writeInventoryCache]);

  // Fetch owned weapons
  const fetchOwnedWeapons = useCallback(async () => {
    if (!publicKey) return;
    const wallet = publicKey.toBase58();

    const cached = readInventoryCache(wallet);
    if (cached?.weapons) setOwnedWeapons(cached.weapons);

    try {
      const response = await axios.get(`${API}/shop/weapons/${wallet}`);
      setOwnedWeapons(response.data);
      writeInventoryCache(wallet, { weapons: response.data });
    } catch (error) {
      console.error('Error fetching owned weapons:', error);
    }
  }, [publicKey, readInventoryCache, writeInventoryCache]);

  // Fetch owned NFT characters
  const fetchOwnedNfts = useCallback(async () => {
    if (!publicKey) return;
    const wallet = publicKey.toBase58();

    const cached = readInventoryCache(wallet);
    if (cached?.nfts) setOwnedNfts(cached.nfts);

    try {
      const response = await axios.get(`${API}/shop/nfts/${wallet}`);
      setOwnedNfts(response.data);
      writeInventoryCache(wallet, { nfts: response.data });
    } catch (error) {
      console.error('Error fetching owned NFTs:', error);
    }
  }, [publicKey, readInventoryCache, writeInventoryCache]);

  // Fetch leaderboard
  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/game/leaderboard?limit=50`);
      setLeaderboardScores(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchSkins();
    fetchLeaderboard();
  }, [fetchSkins, fetchLeaderboard]);

  // Fetch user-specific data when wallet connects
  useEffect(() => {
    if (connected && publicKey) {
      fetchTokenBalance();
      fetchOwnedSkins();
      fetchOwnedWeapons();
      fetchOwnedNfts();
    }
  }, [connected, publicKey, fetchTokenBalance, fetchOwnedSkins, fetchOwnedWeapons, fetchOwnedNfts]);

  // Fetch weapons and NFTs on load
  useEffect(() => {
    fetchWeapons();
    fetchNfts();
  }, [fetchWeapons, fetchNfts]);

  // Auto-select the default free NFT (Foot Stabber) if no NFT is selected
  useEffect(() => {
    if (nfts.length > 0 && !selectedNft) {
      const defaultNft = nfts.find(n => n.is_default || n.price_usd === 0);
      if (defaultNft) {
        setSelectedNft(defaultNft);
      }
    }
  }, [nfts, selectedNft]);

  // Handle skin purchase completed (called after successful transaction)
  const handlePurchaseComplete = async (item) => {
    // Refresh owned skins, weapons, and NFTs after purchase
    await fetchOwnedSkins();
    await fetchOwnedWeapons();
    await fetchOwnedNfts();
    await fetchTokenBalance();
  };

  // Handle skin selection
  const handleSelectSkin = (skin) => {
    setSelectedSkin(skin);
  };

  // Handle game over
  const handleGameOver = async (gameData) => {
    // Tournament score auto-submit
    if (multiplayerSession?.tournamentId && multiplayerSession?.matchId) {
      const pid = publicKey?.toBase58() || (() => {
        let g = sessionStorage.getItem('metal_lego_guest_id');
        if (!g) { g = 'guest_' + Math.random().toString(36).substr(2, 8); sessionStorage.setItem('metal_lego_guest_id', g); }
        return g;
      })();
      try {
        await axios.post(`${API}/tournament/${multiplayerSession.tournamentId}/submit-score`, {
          player_id: pid,
          score: gameData.score,
          kills: gameData.kills,
          level: gameData.level
        });
        toast.success(`Tournament score submitted: ${gameData.score}`);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to submit tournament score');
      }
      // Navigate back to bracket
      setCurrentTournamentId(multiplayerSession.tournamentId);
      setMultiplayerSession(null);
      setCurrentScreen(SCREENS.TOURNAMENT_VIEW);
      return;
    }

    // Resolve player id (wallet or guest) for daily-challenge sync
    const playerId = publicKey?.toBase58() || (() => {
      let g = sessionStorage.getItem('metal_lego_guest_id');
      if (!g) { g = 'guest_' + Math.random().toString(36).substr(2, 8); sessionStorage.setItem('metal_lego_guest_id', g); }
      return g;
    })();

    // Sync daily challenge progress (works for both wallet & guest players)
    try {
      const syncRes = await axios.post(`${API}/challenges/sync-game`, {
        wallet_address: playerId,
        kills: gameData.kills || 0,
        score: gameData.score || 0,
        level: gameData.level || 1,
        time_played: gameData.time_played || 0,
        max_kill_streak: gameData.max_kill_streak || 0,
        bosses_defeated: gameData.bosses_defeated || 0
      });
      const completed = syncRes.data?.newly_completed || [];
      if (completed.length > 0) {
        completed.forEach(c => toast.success(`Challenge complete: ${c.name}!`, { duration: 4000 }));
      }
    } catch (e) {
      console.error('Failed to sync daily challenges:', e);
    }

    // Score submission: wallet users get their score saved to leaderboards;
    // guests get their bestiary/boss roster progress tracked under guest_id.
    const walletIdent = publicKey ? publicKey.toBase58() : (sessionStorage.getItem('metal_lego_guest_id') || null);
    if (!walletIdent) return;

    try {
      const scorePayload = {
        wallet_address: walletIdent,
        score: gameData.score,
        level: gameData.level,
        kills: gameData.kills,
        skin_used: selectedSkin?.id || 'classic',
        kills_by_type: gameData.kills_by_type || {},
        defeated_boss_types: gameData.defeated_boss_types || [],
      };
      await axios.post(`${API}/game/score`, scorePayload);

      // Only refresh leaderboard for wallet players (guests aren't on it)
      if (publicKey) {
        await fetchLeaderboard();
        toast.success('Score saved!');
      }
    } catch (error) {
      console.error('Error saving score:', error);
    }
  };

  // Render current screen
  const renderScreen = () => {
    // Create a modified skin with proxied image URL for game
    const skinWithProxiedImage = selectedSkin ? {
      ...selectedSkin,
      image_url: getProxiedImageUrl(selectedSkin.image_url)
    } : null;

    // Build the in-game weapon loadout from the player's selection
    // (instead of loading every owned weapon)
    const gameLoadout = [];
    if (selectedWeapon && selectedWeapon !== 'none' && ownedWeapons.includes(selectedWeapon)) {
      gameLoadout.push(selectedWeapon);
    } else if (selectedWeapon === 'machine_gun' && ownedWeapons.includes('mini_tank')) {
      // Backward-compat: legacy mini_tank purchase counts as machine_gun
      gameLoadout.push('mini_tank');
    }
    if (bringGrenade && ownedWeapons.includes('grenade')) {
      gameLoadout.push('grenade');
    }

    switch (currentScreen) {
      case SCREENS.GAME:
        return (
          <GameScreen
            selectedSkin={skinWithProxiedImage}
            selectedNft={selectedNft}
            onBack={() => {
              setMultiplayerSession(null);
              setCurrentScreen(SCREENS.MENU);
            }}
            onGameOver={handleGameOver}
            tokenBalance={tokenBalance}
            ownedWeapons={gameLoadout}
            multiplayerSession={multiplayerSession}
          />
        );
      
      case SCREENS.SHOP:
        return (
          <Shop
            skins={skins}
            weapons={weapons}
            nfts={nfts}
            ownedSkins={ownedSkins}
            ownedWeapons={ownedWeapons}
            ownedNfts={ownedNfts}
            selectedSkinId={selectedSkin?.id}
            selectedNftId={selectedNft?.id}
            onSelectSkin={handleSelectSkin}
            onSelectNft={setSelectedNft}
            onPurchase={handlePurchaseComplete}
            onBack={() => setCurrentScreen(SCREENS.MENU)}
            tokenBalance={tokenBalance}
            isLoading={isLoading}
            onRefreshBalance={fetchTokenBalance}
          />
        );
      
      case SCREENS.LEADERBOARD:
        return (
          <Leaderboard
            scores={leaderboardScores}
            onBack={() => setCurrentScreen(SCREENS.MENU)}
            currentWallet={publicKey?.toBase58()}
            onViewProfile={(pid) => {
              if (!pid) return;
              setProfilePlayerId(pid);
              setProfileReturnScreen(SCREENS.LEADERBOARD);
              setCurrentScreen(SCREENS.PROFILE);
              navigate(`/profile/${encodeURIComponent(pid)}`);
            }}
          />
        );
      
      case SCREENS.CHALLENGES:
        return (
          <DailyChallenges
            onBack={() => setCurrentScreen(SCREENS.MENU)}
          />
        );

      case SCREENS.BOSS_ROSTER:
        return (
          <BossRoster
            walletAddress={publicKey?.toBase58()}
            onBack={() => setCurrentScreen(SCREENS.MENU)}
          />
        );

      case SCREENS.BESTIARY:
        return (
          <Bestiary
            walletAddress={publicKey?.toBase58()}
            onBack={() => setCurrentScreen(SCREENS.MENU)}
          />
        );
      
      case SCREENS.MULTIPLAYER:
        return (
          <LobbyBrowser
            onBack={() => setCurrentScreen(SCREENS.MENU)}
            onJoinLobby={(lobbyId) => {
              setCurrentLobbyId(lobbyId);
              setCurrentScreen(SCREENS.LOBBY);
            }}
            onCreateLobby={(lobbyId) => {
              setCurrentLobbyId(lobbyId);
              setCurrentScreen(SCREENS.LOBBY);
            }}
          />
        );
      
      case SCREENS.LOBBY:
        return (
          <MultiplayerLobby
            lobbyId={currentLobbyId}
            onBack={() => {
              setCurrentLobbyId(null);
              setCurrentScreen(SCREENS.MULTIPLAYER);
            }}
            onStartGame={(sessionId, lobby) => {
              setMultiplayerSession({ sessionId, lobby });
              setCurrentScreen(SCREENS.GAME);
            }}
          />
        );
      
      case SCREENS.TOURNAMENTS:
        return (
          <TournamentBrowser
            onBack={() => setCurrentScreen(SCREENS.MENU)}
            onViewTournament={(id) => {
              setCurrentTournamentId(id);
              setCurrentScreen(SCREENS.TOURNAMENT_VIEW);
              navigate(`/tournament/${encodeURIComponent(id)}`);
            }}
          />
        );
      
      case SCREENS.TOURNAMENT_VIEW:
        return (
          <TournamentBracket
            tournamentId={currentTournamentId}
            onBack={() => {
              setCurrentTournamentId(null);
              setCurrentScreen(SCREENS.TOURNAMENTS);
              navigate('/');
            }}
            onPlayMatch={(tournamentId, matchId, timeLimit) => {
              setMultiplayerSession({ tournamentId, matchId, timeLimit });
              setCurrentScreen(SCREENS.GAME);
            }}
            onSpectateMatch={(lobbyId, matchIdParam) => {
              setSpectatingLobbyId(lobbyId);
              setSpectatingTournamentCtx({ tournamentId: lobbyId, matchId: matchIdParam });
              setCurrentScreen(SCREENS.SPECTATE_MATCH);
            }}
          />
        );
      
      case SCREENS.SPECTATE:
        return (
          <SpectatorBrowser
            onBack={() => setCurrentScreen(SCREENS.MENU)}
            onWatch={(lobbyId) => {
              setSpectatingLobbyId(lobbyId);
              setCurrentScreen(SCREENS.SPECTATE_MATCH);
            }}
          />
        );
      
      case SCREENS.SPECTATE_MATCH:
        return (
          <SpectatorCanvas
            lobbyId={spectatingLobbyId}
            tournamentId={spectatingTournamentCtx?.tournamentId}
            matchId={spectatingTournamentCtx?.matchId}
            onBack={() => {
              setSpectatingLobbyId(null);
              setSpectatingTournamentCtx(null);
              setCurrentScreen(SCREENS.SPECTATE);
            }}
          />
        );
      
      case SCREENS.PROFILE:
        return (
          <PlayerProfile
            playerId={profilePlayerId}
            onBack={() => {
              setProfilePlayerId(null);
              setCurrentScreen(profileReturnScreen);
              setProfileReturnScreen(SCREENS.MENU);
              navigate('/');
            }}
          />
        );

      case SCREENS.LOADOUT:
        return (
          <LoadoutSelector
            skins={skins}
            ownedSkins={ownedSkins}
            selectedSkin={selectedSkin}
            onSelectSkin={setSelectedSkin}
            ownedWeapons={ownedWeapons}
            selectedWeapon={selectedWeapon}
            onSelectWeapon={setSelectedWeapon}
            bringGrenade={bringGrenade}
            onToggleGrenade={setBringGrenade}
            onBack={() => setCurrentScreen(SCREENS.MENU)}
            onShop={() => setCurrentScreen(SCREENS.SHOP)}
          />
        );
      
      default:
        return (
          <MainMenu
            onPlay={() => setCurrentScreen(SCREENS.GAME)}
            onShop={() => setCurrentScreen(SCREENS.SHOP)}
            onLeaderboard={() => setCurrentScreen(SCREENS.LEADERBOARD)}
            onChallenges={() => setCurrentScreen(SCREENS.CHALLENGES)}
            onMultiplayer={() => setCurrentScreen(SCREENS.MULTIPLAYER)}
            onTournaments={() => setCurrentScreen(SCREENS.TOURNAMENTS)}
            onSpectate={() => setCurrentScreen(SCREENS.SPECTATE)}
            onBossRoster={() => setCurrentScreen(SCREENS.BOSS_ROSTER)}
            onBestiary={() => setCurrentScreen(SCREENS.BESTIARY)}
            onProfile={(pid) => {
              const guestId = sessionStorage.getItem('metal_lego_guest_id') || 'guest_' + Math.random().toString(36).substr(2, 8);
              if (!sessionStorage.getItem('metal_lego_guest_id')) sessionStorage.setItem('metal_lego_guest_id', guestId);
              const finalPid = pid || publicKey?.toBase58() || guestId;
              setProfilePlayerId(finalPid);
              setCurrentScreen(SCREENS.PROFILE);
              navigate(`/profile/${encodeURIComponent(finalPid)}`);
            }}
            onLoadout={() => setCurrentScreen(SCREENS.LOADOUT)}
            tokenBalance={tokenBalance}
            selectedSkin={selectedSkin}
            selectedWeapon={selectedWeapon}
            bringGrenade={bringGrenade}
          />
        );
    }
  };

  return (
    <div className="App" data-testid="game-app">
      {renderScreen()}
      <Toaster 
        position="top-center" 
        theme="dark"
        toastOptions={{
          style: {
            background: '#111111',
            border: '1px solid #14F195',
            color: '#fff'
          }
        }}
      />
    </div>
  );
};

function App() {
  return (
    <SolanaProvider>
      <BrowserRouter>
        <Routes>
          {/* Deep-linkable routes hydrate state inside GameApp via useLocation */}
          <Route path="/profile/:id" element={<GameApp />} />
          <Route path="/tournament/:id" element={<GameApp />} />
          <Route path="*" element={<GameApp />} />
        </Routes>
      </BrowserRouter>
    </SolanaProvider>
  );
}

export default App;
